import React, { useEffect, useMemo, useState } from 'react';

import {
  Anchor,
  Badge,
  createStyles,
  Group,
  Loader,
  Stack,
  Table as MantineTable,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { IconChevronLeft, IconTrophy } from '@tabler/icons-react';
import MovementTypeBadge from 'components/status-badges/MovementTypeBadge';
import Table from 'components/Table';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import useMovementHistory from 'hooks/workoutHistory/useMovementHistory';
import useMovementHistoryEntries from 'hooks/workoutHistory/useMovementHistoryEntries';
import { CellProps } from 'react-table';
import { WorkoutHistoryDetail, WorkoutHistoryEntry, WorkoutHistoryRow } from 'types';
import { SCORE_TYPE_LABEL_MAP, ScoreTypeEnum, WORKOUT_LEVEL_LABEL_MAP, WorkoutLevelEnum } from 'types/enums';
import { formatDateForDisplay } from 'utils/date-functions';

import WorkoutSidePanel from './WorkoutSidePanel';

const useStyles = createStyles(theme => ({
  card: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    padding: theme.spacing.md,
  },
  scroll: {
    overflowX: 'auto',
  },
}));

interface MovementHistoryDetailProps {
  athleteId: string;
  row: WorkoutHistoryRow;
  back: () => void;
}

const ReferenceLiftCard = ({ detail }: { detail: WorkoutHistoryDetail }): JSX.Element | null => {
  const { classes } = useStyles();
  const reference = detail.referenceLift;

  // Absent, not zeroed: every set above the rep cap means no reliable projection.
  if (!reference) {
    return null;
  }

  return (
    <div className={classes.card}>
      <Text size='xs' color='dimmed' tt='uppercase'>
        Estimated 1RM
      </Text>
      <Title order={3}>{reference.estimated1rm}</Title>
      <Text size='sm' color='dimmed'>
        Brzycki formula, from {reference.weight} &times; {reference.reps} on{' '}
        {formatDateForDisplay(reference.date)}
      </Text>

      {detail.percentageTable.length > 0 && (
        <div className={classes.scroll}>
          <MantineTable mt='md' fontSize='sm'>
            <thead>
              <tr>
                <th>Percentage</th>
                <th>Weight</th>
              </tr>
            </thead>
            <tbody>
              {detail.percentageTable.map(step => (
                <tr key={step.percentage}>
                  <td>{step.percentage}%</td>
                  <td>{step.weight}</td>
                </tr>
              ))}
            </tbody>
          </MantineTable>
        </div>
      )}
    </div>
  );
};

/**
 * One movement's history, shown in place of the list rather than over it.
 *
 * Two reads on one key object: the header, and the entries a page at a time. The name
 * comes from the row this was opened from, so the header payload carries none.
 */
const MovementHistoryDetail = ({ athleteId, row, back }: MovementHistoryDetailProps): JSX.Element => {
  const theme = useMantineTheme();
  const { trackWorkoutHistoryWorkoutOpened } = useStaffTracking();
  const [panelEntry, setPanelEntry] = useState<WorkoutHistoryEntry | null>(null);
  const [panelOpened, setPanelOpened] = useState(false);
  const [tableData, setTableData] = useState<WorkoutHistoryEntry[]>([]);

  const timeline = useMemo(
    () => ({
      athleteId,
      movementId: row.workoutMovementId ?? undefined,
      scoreType: row.scoreType,
    }),
    [athleteId, row.workoutMovementId, row.scoreType],
  );

  const { data: headerData, isLoading: headerLoading, isError: headerError } = useMovementHistory(timeline);
  const {
    data: entriesData,
    isLoading: entriesLoading,
    isError: entriesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMovementHistoryEntries(timeline);

  const detail = headerData?.data;
  const totalEntries = entriesData?.pages[0]?.data.count ?? 0;

  // Mirrored into state like the landing table, so a refetch does not flash empty.
  useEffect(() => {
    if (!entriesLoading && entriesData) {
      setTableData(entriesData.pages.map(page => page.data.results).flat());
    }
  }, [entriesLoading, entriesData]);

  // Only an entry whose workout still exists is openable, so the id is always present.
  const openWorkout = (entry: WorkoutHistoryEntry): void => {
    setPanelEntry(entry);
    setPanelOpened(true);
    trackWorkoutHistoryWorkoutOpened({ workout_id: entry.workoutId ?? '' });
  };

  const entryColumns = [
    {
      Header: 'Date',
      accessor: 'date' as const,
      Cell: ({ row: entryRow }: CellProps<WorkoutHistoryEntry>) =>
        entryRow.original.workoutId ? (
          // A workout that is gone leaves the entry readable, without the link.
          <Anchor component='button' type='button' size='sm' onClick={() => openWorkout(entryRow.original)}>
            {formatDateForDisplay(entryRow.original.date)}
          </Anchor>
        ) : (
          <Text size='sm'>{formatDateForDisplay(entryRow.original.date)}</Text>
        ),
    },
    {
      Header: 'Result',
      accessor: 'result' as const,
      Cell: ({ row: entryRow }: CellProps<WorkoutHistoryEntry>) => (
        <Group spacing='xs' noWrap>
          <Text size='sm'>{entryRow.original.result}</Text>
          {entryRow.original.isPr && (
            <IconTrophy
              size={theme.fontSizes.md}
              color={theme.colors.yellow[6]}
              aria-label='Personal record'
            />
          )}
          {entryRow.original.level && (
            <Badge variant='outline' size='xs' color='gray'>
              {WORKOUT_LEVEL_LABEL_MAP[entryRow.original.level as WorkoutLevelEnum]}
            </Badge>
          )}
        </Group>
      ),
    },
    {
      Header: 'Sets',
      accessor: 'setCount' as const,
      Cell: ({ row: entryRow }: CellProps<WorkoutHistoryEntry>) => (
        <Text size='sm'>{entryRow.original.setCount || '—'}</Text>
      ),
    },
  ];

  return (
    <Stack spacing='lg' p='xl'>
      <div>
        <Anchor component='button' type='button' size='sm' onClick={back}>
          <Group spacing={4} noWrap>
            <IconChevronLeft size={14} />
            Historical Performance
          </Group>
        </Anchor>
        <Group spacing='sm' mt='xs' align='center'>
          <Title order={2}>{row.name}</Title>
          <MovementTypeBadge movementType={row.movementType} />
          <Text size='sm' color='dimmed'>
            {SCORE_TYPE_LABEL_MAP[row.scoreType as ScoreTypeEnum] ?? row.scoreType}
          </Text>
        </Group>
      </div>

      {headerError || entriesError ? (
        <Text size='sm' color='dimmed' py='xl' align='center'>
          This history could not be loaded. Go back and try again.
        </Text>
      ) : headerLoading || !detail ? (
        <Group position='center' py='xl'>
          <Loader />
        </Group>
      ) : (
        <>
          {detail.description && (
            // The description is authored as rich text and sanitized server-side by
            // WorkoutMovementManager, so it renders as markup the way notes do.
            <Text
              size='sm'
              color='dimmed'
              sx={{ wordBreak: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: detail.description }}
            />
          )}

          <ReferenceLiftCard detail={detail} />

          <div>
            <Group position='apart' align='baseline' mb='xs'>
              <Text size='xs' color='dimmed' tt='uppercase'>
                Historical results
              </Text>
              {totalEntries > 0 && (
                <Group spacing='xs'>
                  <Text size='xs' color='dimmed'>
                    {totalEntries} {totalEntries === 1 ? 'result' : 'results'}
                  </Text>
                  {row.scoreType === ScoreTypeEnum.WEIGHT_SETS_REPS && (
                    <Text size='xs' color='dimmed'>
                      · weights shown in {detail.unit}, the gym&apos;s unit
                    </Text>
                  )}
                </Group>
              )}
            </Group>

            <Table
              tableData={tableData}
              columns={entryColumns}
              isSuccess={!entriesLoading}
              isLoading={entriesLoading}
              isError={entriesError}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={!!hasNextPage}
              fetchNextPage={fetchNextPage}
              margin={0}
              EmptyState={
                <Text size='sm' color='dimmed'>
                  No results logged yet.
                </Text>
              }
            />
          </div>
        </>
      )}

      <WorkoutSidePanel
        athleteId={athleteId}
        movementName={row.name}
        entry={panelEntry}
        opened={panelOpened}
        close={() => setPanelOpened(false)}
      />
    </Stack>
  );
};

export default MovementHistoryDetail;
